import type {
  UseComboboxPropGetters,
  UseMultipleSelectionActions,
  UseMultipleSelectionPropGetters,
} from 'downshift';
import {createContext, useContext} from 'react';
import type {Dispatch, KeyboardEvent, RefObject, SetStateAction} from 'react';

type MultiComboBoxInfiniteLoader = () => Promise<boolean> | boolean;
type MultiComboBoxContextType<T> = {
  isOpen: boolean;
  as?: 'MultiSelect' | 'ComboBox';
  clearable: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  filteredItems: T[];
  selectedItems: T[];
  customItemsList: T[];
  inputValue: string;
  itemToString: (item: T | null) => string;
  isLoading: boolean;
  loadItems?: (inputValue: string) => Promise<{items: T[]; hasMore: boolean}>;
  isItemDisabled?: (item: T) => boolean;
  setSelectedItems: Dispatch<SetStateAction<T[]>>;
  setItemsList: Dispatch<SetStateAction<T[]>>;
  setInputValue: Dispatch<SetStateAction<string>>;
  handleOnSearchKeyDown: (evt: KeyboardEvent<HTMLInputElement>) => void;
  closeMenu: () => void;
  openMenu: () => void;
  toggleMenu: () => void;
  getMenuProps: UseComboboxPropGetters<T>['getMenuProps'];
  getInputProps: UseComboboxPropGetters<T>['getInputProps'];
  getLabelProps: UseComboboxPropGetters<T>['getLabelProps'];
  getItemProps: UseComboboxPropGetters<T>['getItemProps'];
  getSelectedItemProps: UseMultipleSelectionPropGetters<T>['getSelectedItemProps'];
  getDropdownProps: UseMultipleSelectionPropGetters<T>['getDropdownProps'];
  getToggleButtonProps: UseComboboxPropGetters<T>['getToggleButtonProps'];
  removeSelectedItem: UseMultipleSelectionActions<T>['removeSelectedItem'];
  registerInfiniteLoader: (loader: MultiComboBoxInfiniteLoader | null) => void;
  infiniteLoaderRef: {current: MultiComboBoxInfiniteLoader | null};
  hasExternalInfiniteLoader: boolean;
  anchorPlaceholder?: string;
  toggleAriaLabel?: string;
  emptyStateMessage: string;
};

const MultiComboBoxContext =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createContext<MultiComboBoxContextType<any> | null>(null);
const useMultiComboBoxContext = <T,>(): MultiComboBoxContextType<T> => {
  const context = useContext(MultiComboBoxContext);
  if (!context) {
    throw new Error(
      'useMultiComboBoxContext must be used within MultiComboBoxContextProvider'
    );
  }
  return context as MultiComboBoxContextType<T>;
};

export {MultiComboBoxContext, useMultiComboBoxContext};
export type {MultiComboBoxContextType, MultiComboBoxInfiniteLoader};
